﻿/**
 * فرم ورودی قضاوت مقایسه‌ای: زمینه و هدف مشترک، سپس دو گزینه‌ی هم‌شکل.
 *
 * ⚠️ **چرا زمینه مشترک است و در هر گزینه تکرار نمی‌شود:** اگر هر گزینه زمینه‌ی
 * خودش را داشت، کاربر ناچار بود اطلاعات یکسان را دو بار بنویسد و هر اختلاف
 * جزئی میان آن دو، به یک تفاوتِ ساختگی در مقایسه تبدیل می‌شد.
 *
 * ⚠️ **چرا فیلدهای دو کارت یکسان‌اند:** توصیف نامتقارن، مقایسه را به کیفیت
 * *نوشتن* کاربر گره می‌زد، نه به کیفیت تصمیم. ساختار یکسان این را کم می‌کند.
 */

import { GitCompareArrows, Scale } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/Button'
import { Banner, Card, SectionHeader } from '@/components/ui/primitives'
import { useMeta } from '@/lib/meta-context'

import type { JudgmentOption, JudgmentInput } from '@/lib/types'

const EMPTY_OPTION: JudgmentOption = {
  title: '',
  description: '',
  cost: '',
  timeline: '',
  assumptions: '',
  reversibility: '',
}

interface DecisionPairFormProps {
  onSubmit: (payload: JudgmentInput) => void
  isBusy: boolean
}

export const DecisionPairForm = ({ onSubmit, isBusy }: DecisionPairFormProps) => {
  const { meta } = useMeta()
  const [sharedContext, setSharedContext] = useState('')
  const [sharedGoal, setSharedGoal] = useState('')
  const [criteria, setCriteria] = useState('')
  const [optionA, setOptionA] = useState<JudgmentOption>(EMPTY_OPTION)
  const [optionB, setOptionB] = useState<JudgmentOption>(EMPTY_OPTION)

  const filled = (option: JudgmentOption): boolean =>
    option.title.trim() !== '' || option.description.trim() !== ''
  const ready =
    sharedContext.trim() !== '' && filled(optionA) && filled(optionB) && !isBusy

  return (
    <div className="space-y-4">
      <Banner tone="info">{meta.profile.disclaimer}</Banner>

      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault()
          if (!ready) {
            return
          }
          onSubmit({
            shared_context: sharedContext,
            shared_goal: sharedGoal,
            option_a: optionA,
            option_b: optionB,
            criteria,
          })
        }}
      >
        <Card tone="feature">
          <SectionHeader
            icon={<Scale className="size-4" aria-hidden />}
            title="زمینه و هدف مشترک"
            caption="یک بار نوشته می‌شود و برای هر دو گزینه به کار می‌رود. تکرارش در هر گزینه، تفاوت ساختگی می‌سازد."
          />
          <div className="space-y-4 p-5">
            <Field
              id="shared-context"
              label="زمینه‌ی مشترک"
              hint="وضعیت فعلی، محدودیت‌ها، ذی‌نفعان و فشار زمانی."
              rows={5}
              value={sharedContext}
              onChange={setSharedContext}
            />
            <Field
              id="shared-goal"
              label="هدف مشترک"
              hint="موفقیت دقیقاً چه شکلی است؟ هر دو گزینه در برابر همین سنجیده می‌شوند."
              rows={3}
              value={sharedGoal}
              onChange={setSharedGoal}
            />
          </div>
        </Card>

        <div className="grid gap-4 lg:grid-cols-2">
          <OptionCard label="تصمیم الف" slug="a" value={optionA} onChange={setOptionA} />
          <OptionCard label="تصمیم ب" slug="b" value={optionB} onChange={setOptionB} />
        </div>

        <Card>
          <SectionHeader
            title="معیارها و خط قرمزها"
            caption="اختیاری. اگر معیار یا خط قرمز مشخصی دارید، وزن‌دهی را همان جهت می‌دهد."
          />
          <div className="p-5">
            <Field
              id="criteria"
              label="معیارهای مهم شما"
              rows={3}
              value={criteria}
              onChange={setCriteria}
            />
          </div>
        </Card>

        <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-surface-raised px-5 py-4 shadow-raised">
          <p className="max-w-md text-xs leading-relaxed text-ink-faint">
            هر دو گزینه باید حداقل عنوان یا شرح داشته باشند. نتیجه می‌گوید کدام به منطق
            تصمیم‌گیر نزدیک‌تر است، و اگر هیچ‌کدام مناسب نبود همان را می‌گوید.
          </p>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            icon={<GitCompareArrows className="size-4" aria-hidden />}
            loading={isBusy}
            disabled={!ready}
          >
            مقایسه و قضاوت
          </Button>
        </div>
      </form>
    </div>
  )
}

interface OptionCardProps {
  label: string
  slug: string
  value: JudgmentOption
  onChange: (next: JudgmentOption) => void
}

/** یک کارت گزینه. ترتیب و مجموعه‌ی فیلدها برای هر دو گزینه یکی است. */
const OptionCard = ({ label, slug, value, onChange }: OptionCardProps) => {
  const patch = (part: Partial<JudgmentOption>): void => {
    onChange({ ...value, ...part })
  }
  return (
    <Card>
      <SectionHeader title={label} />
      <div className="space-y-3 p-5">
        <Line
          id={`option-${slug}-title`}
          label="عنوان"
          value={value.title}
          onChange={(next) => {
            patch({ title: next })
          }}
        />
        <Field
          id={`option-${slug}-description`}
          label="شرح اقدام"
          rows={4}
          value={value.description}
          onChange={(next) => {
            patch({ description: next })
          }}
        />
        <Line
          id={`option-${slug}-cost`}
          label="هزینه"
          hint="با واحد بنویسید."
          value={value.cost}
          onChange={(next) => {
            patch({ cost: next })
          }}
        />
        <Line
          id={`option-${slug}-timeline`}
          label="زمان اجرا"
          value={value.timeline}
          onChange={(next) => {
            patch({ timeline: next })
          }}
        />
        <Line
          id={`option-${slug}-reversibility`}
          label="برگشت‌پذیری"
          value={value.reversibility}
          onChange={(next) => {
            patch({ reversibility: next })
          }}
        />
        <Field
          id={`option-${slug}-assumptions`}
          label="فرض‌ها و ریسک‌های اختصاصی"
          rows={2}
          value={value.assumptions}
          onChange={(next) => {
            patch({ assumptions: next })
          }}
        />
      </div>
    </Card>
  )
}

interface FieldProps {
  id: string
  label: string
  hint?: string
  rows: number
  value: string
  onChange: (next: string) => void
}

const Field = ({ id, label, hint, rows, value, onChange }: FieldProps) => (
  <div className="space-y-1.5">
    <label htmlFor={id} className="text-xs font-semibold text-ink">
      {label}
    </label>
    <textarea
      id={id}
      rows={rows}
      value={value}
      onChange={(event) => {
        onChange(event.target.value)
      }}
      className="w-full resize-y rounded-md border border-line-strong bg-surface-raised px-3.5 py-2.5 text-sm leading-relaxed text-ink shadow-raised transition-[border-color,box-shadow] placeholder:text-ink-faint focus:border-accent focus:ring-4 focus:ring-accent/15 focus:outline-none"
    />
    {hint !== undefined && (
      <p className="text-xs leading-relaxed text-ink-faint">{hint}</p>
    )}
  </div>
)

interface LineProps {
  id: string
  label: string
  hint?: string
  value: string
  onChange: (next: string) => void
}

const Line = ({ id, label, hint, value, onChange }: LineProps) => (
  <div className="space-y-1.5">
    <label htmlFor={id} className="text-xs font-semibold text-ink">
      {label}
    </label>
    <input
      id={id}
      value={value}
      onChange={(event) => {
        onChange(event.target.value)
      }}
      className="h-10 w-full rounded-md border border-line-strong bg-surface-raised px-3.5 text-sm text-ink shadow-raised transition-[border-color,box-shadow] placeholder:text-ink-faint focus:border-accent focus:ring-4 focus:ring-accent/15 focus:outline-none"
    />
    {hint !== undefined && (
      <p className="text-xs leading-relaxed text-ink-faint">{hint}</p>
    )}
  </div>
)
